import { useState } from 'react'
import { Bell, X } from 'lucide-react'
import { usePushNotifications } from '../utils/usePushNotifications'

const PROMPT_DAY_KEY = 'notify_prompt_day'

function today() {
  // Local YYYY-MM-DD (timezone-safe vs toISOString which is UTC).
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function dismissedToday() {
  try { return localStorage.getItem(PROMPT_DAY_KEY) === today() } catch { return false }
}

/**
 * Inline opt-in card for push notifications, shown near the top of the home
 * (Jobs) page. Works for GUESTS too — subscribing is anonymous-friendly.
 *
 * Shows ONLY when push is supported, permission is still 'default' (undecided),
 * the user is not already subscribed, and it hasn't been dismissed today.
 * Renders nothing if permission is 'denied'.
 *
 * Permission is requested only on the button click (user gesture) — never on
 * mount — so browsers don't block it.
 */
export default function HomeNotifyPrompt() {
  const { supported, permission, subscribed, loading, requestAndSubscribe } = usePushNotifications()
  const [dismissed, setDismissed] = useState(dismissedToday)

  if (!supported) return null
  if (permission !== 'default') return null   // already granted or denied
  if (subscribed) return null
  if (dismissed) return null

  const dismiss = () => {
    try { localStorage.setItem(PROMPT_DAY_KEY, today()) } catch {}
    setDismissed(true)
  }

  const enable = async () => {
    const ok = await requestAndSubscribe()
    if (ok) setDismissed(true)            // hide on success
    // On failure the hook flips `permission` to 'denied' (→ unmounts) or keeps
    // 'default'; either way we leave the card so the user can retry.
  }

  return (
    <div
      className="fade-up"
      style={{
        display: 'flex', alignItems: 'center', gap: '.85rem',
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '.9rem 1rem',
        marginBottom: '1.5rem',
      }}
    >
      <div style={{ fontSize: '1.6rem', flexShrink: 0, lineHeight: 1 }}>🔔</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '.92rem', lineHeight: 1.35, color: 'var(--text)' }}>
          Yeni elanlar və müraciətlərdən dərhal xəbərdar olun
        </div>
        <div style={{ fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.45, marginTop: '.15rem' }}>
          Bildirişləri aktivləşdirin — qaçırmayın.
        </div>
      </div>

      <button
        onClick={enable}
        className="btn btn-ghost btn-sm"
        disabled={loading}
        style={{ fontSize: '.82rem', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {loading
          ? <span className="spinner" />
          : (<><Bell size={14} style={{ marginRight: 4 }} /> Bildirişləri aç</>)}
      </button>

      <button
        onClick={dismiss}
        aria-label="Bağla"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--muted)', padding: '.2rem', flexShrink: 0,
          display: 'flex', alignItems: 'center',
        }}
      >
        <X size={16} />
      </button>
    </div>
  )
}
